// src/components/ClientBooking.js
import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import tchHeaderLogo from "../assets/tch-header-logo.png";

// ---------- Small dumb field components (no internal state) ----------
const BookingInput = ({
  label,
  name,
  type = "text",
  required = false,
  value,
  onChange,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      name={name}
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:ring-amber-500 focus:border-amber-500 outline-none"
    />
  </div>
);

const BookingTextArea = ({
  label,
  name,
  required = false,
  value,
  onChange,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      name={name}
      rows={3}
      required={required}
      value={value}
      onChange={onChange}
      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm resize-none focus:ring-amber-500 focus:border-amber-500 outline-none"
    />
  </div>
);

const BookingSelect = ({
  label,
  name,
  required = false,
  value,
  onChange,
  options,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      required={required}
      value={value}
      onChange={onChange}
      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm bg-white focus:ring-amber-500 focus:border-amber-500 outline-none"
    >
      <option value="">Select...</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </div>
);

// ---------- Main component ----------
const ClientBooking = () => {
  const [form, setForm] = useState({
    fullName: "",
    phone: "",
    email: "",
    faculty: "",
    nextOfKin: "",
    guarantor: "",
    gender: "",
    passportId: "",
    arrivalDate: "",
    departureDate: "",
    desiredRoom: "",
    notes: "",
  });

  const [rooms, setRooms] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Load rooms
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const q = query(
          collection(db, "rooms"),
          where("status", "==", "Available")
        );
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRooms(list);
      } catch (err) {
        console.error("Rooms load error:", err);
      }
    };
    fetchRooms();
  }, []);

  // Single stable change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit booking
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    setSubmitting(true);

    try {
      if (!form.fullName || !form.phone || !form.desiredRoom) {
        setErrorMsg("Please fill all required fields (*)");
        setSubmitting(false);
        return;
      }

      await addDoc(collection(db, "bookings"), {
        ...form,
        createdAt: new Date(),
        status: "Pending",
      });

      setSuccessMsg("Your booking request has been submitted.");
      setForm({
        fullName: "",
        phone: "",
        email: "",
        faculty: "",
        nextOfKin: "",
        guarantor: "",
        gender: "",
        passportId: "",
        arrivalDate: "",
        departureDate: "",
        desiredRoom: "",
        notes: "",
      });
    } catch (err) {
      console.error(err);
      setErrorMsg("Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-rose-50">
      {/* HEADER – preserved exactly */}
      <header className="bg-[#b4642d] text-white border-b border-[#f0c96a]">
        <div className="mx-auto max-w-5xl px-6 py-4 flex justify-between items-center">
          <img
            src={tchHeaderLogo}
            alt="Tender Castle Hostel - Comfort Living Perfected"
            className="h-12 md:h-14 w-auto"
          />
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="max-w-5xl mx-auto px-4 py-6 md:py-10">
        <div className="bg-white/95 rounded-2xl shadow-xl border border-amber-50 p-6 md:p-8">
          {/* TITLE */}
          <h1 className="text-xl md:text-2xl font-bold mb-4 text-gray-900">
            Hostel Booking Request
          </h1>
          <p className="mb-4 text-sm md:text-base text-gray-600">
            Kindly fill in your details to request a room at{" "}
            <span className="font-semibold">Tender Castle Hostel</span>.
          </p>

          {/* MESSAGES */}
          {errorMsg && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
              {successMsg}
            </div>
          )}

          {/* FORM */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* PERSONAL INFO */}
            <section>
              <h2 className="text-lg font-semibold mb-3 text-gray-800">
                Personal Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <BookingInput
                  label="Full Name"
                  name="fullName"
                  required
                  value={form.fullName}
                  onChange={handleChange}
                />
                <BookingInput
                  label="Phone"
                  name="phone"
                  required
                  value={form.phone}
                  onChange={handleChange}
                />
                <BookingInput
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                />
                <BookingInput
                  label="Faculty / Department"
                  name="faculty"
                  value={form.faculty}
                  onChange={handleChange}
                />
                <BookingSelect
                  label="Gender"
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  options={[
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                  ]}
                />
                <BookingInput
                  label="Passport / ID Number"
                  name="passportId"
                  value={form.passportId}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* EMERGENCY CONTACT */}
            <section>
              <h2 className="text-lg font-semibold mb-3 text-gray-800">
                Emergency Contact
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <BookingInput
                  label="Next of Kin"
                  name="nextOfKin"
                  value={form.nextOfKin}
                  onChange={handleChange}
                />
                <BookingInput
                  label="Guarantor"
                  name="guarantor"
                  value={form.guarantor}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* BOOKING DETAILS */}
            <section>
              <h2 className="text-lg font-semibold mb-3 text-gray-800">
                Booking Details
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <BookingInput
                  label="Arrival Date"
                  name="arrivalDate"
                  type="date"
                  required
                  value={form.arrivalDate}
                  onChange={handleChange}
                />
                <BookingInput
                  label="Departure Date"
                  name="departureDate"
                  type="date"
                  required
                  value={form.departureDate}
                  onChange={handleChange}
                />

                {/* ROOM SELECT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Desired Room Number <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="desiredRoom"
                    required
                    value={form.desiredRoom}
                    onChange={handleChange}
                    disabled={rooms.length === 0}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm bg-white focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100 disabled:text-gray-500"
                  >
                    <option value="">
                      {rooms.length === 0
                        ? "No rooms currently available"
                        : "Select an available room"}
                    </option>
                    {rooms.map((r) => (
                      <option key={r.id} value={r.roomNo}>
                        {r.roomNo} – ₦
                        {r.price ? Number(r.price).toLocaleString() : "N/A"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            {/* NOTES */}
            <section>
              <BookingTextArea
                label="Additional Notes / Requests"
                name="notes"
                value={form.notes}
                onChange={handleChange}
              />
              <p className="mt-1 text-xs text-gray-500">
                You can mention preferred floor, special needs, or any other
                details we should know.
              </p>
            </section>

            <p className="text-xs text-gray-500 text-center">
              Bookings are subject to availability &amp; confirmation.
            </p>

            <button
              type="submit"
              disabled={submitting}
              className="w-full md:w-auto px-5 py-2.5 rounded-md bg-[#b4642d] text-white font-semibold shadow hover:bg-[#9b5525] disabled:opacity-50"
            >
              {submitting ? "Submitting..." : "Submit Booking Request"}
            </button>
          </form>
        </div>

        <p className="mt-4 text-xs text-gray-500 text-center">
          By submitting this form, you consent to be contacted by Tender Castle
          Hostel regarding your booking request.
        </p>
      </main>
    </div>
  );
};

export default ClientBooking;
